import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { Logo } from "@/components/Logo";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api/client";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowResend(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setError(detail.message);
        setShowResend(true);
      } else {
        setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email, origin_url: window.location.origin });
      toast.success("Verification email sent - check your inbox.");
    } catch (e) {
      toast.error("Could not resend the verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="login-page">
      <BlueprintBackground />
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="login-logo-link">
          <Logo size={26} />
          <Wordmark className="font-heading text-xl font-bold tracking-tight text-white" />
        </Link>
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-md p-8 shadow-2xl">
          <h1 className="font-heading text-2xl font-bold text-white mb-1">Welcome back</h1>
          <p className="text-slate-400 text-sm mb-6">Log in to access your architecture projects.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Link to="/forgot-password" className="text-xs text-blue-400 hover:text-blue-300" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div>
                <p className="text-sm text-rose-400" data-testid="login-error-message">{error}</p>
                {showResend && (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending}
                    data-testid="login-resend-verification-button"
                    className="text-sm text-blue-400 hover:text-blue-300 font-medium mt-1.5"
                  >
                    {resending ? "Sending..." : "Resend verification email"}
                  </button>
                )}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Log In
            </Button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            Don't have an account?{" "}
            <Link to="/signup" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="login-signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
